<template>
  <el-row :gutter="16" class="data-report-overview">
    <el-col :xs="24" :md="12">
      <ds-chart-panel
        :title="$t('dataReport.floorRankTitle')"
        :subtitle="$t('dataReport.floorRankSubtitle')"
        :loading="loadingFloor"
        :empty="floorEmpty"
        :summary="floorSummary">
        <ds-report-chart :option="floorBarOption" height="320px"></ds-report-chart>
        <template slot="empty">
          <ds-empty-state type="no-data" :description="$t('dataReport.floorRankNoData')"></ds-empty-state>
        </template>
      </ds-chart-panel>
    </el-col>
    <el-col :xs="24" :md="12">
      <ds-chart-panel
        :title="$t('dataReport.earnedWayChartTitle')"
        :subtitle="$t('dataReport.earnedWayChartSubtitle')"
        :loading="loadingWay"
        :empty="wayEmpty"
        :summary="waySummary">
        <ds-report-chart :option="wayPieOption" height="320px"></ds-report-chart>
        <template slot="empty">
          <ds-empty-state type="no-data" :description="$t('dataReport.earnedWayNoData')"></ds-empty-state>
        </template>
      </ds-chart-panel>
    </el-col>
  </el-row>
</template>

<script>
import { queryReceivedStatistics, queryReceivedWayStatistics } from '@/api/report/dataReportApi'
import DsChartPanel from '@/components/ds/ds-chart-panel.vue'
import DsReportChart from '@/components/ds/ds-report-chart.vue'
import DsEmptyState from '@/components/ds/ds-empty-state.vue'
import { formatMoney } from '@/utils/format'

/**
 * DataReportOverviewCharts —— 概览图表区（趋势与构成）
 *
 * 数据口径说明（不新增任何接口、不改变任何业务计算口径）：
 *  - 楼栋实收排行：queryReceivedStatistics（按楼栋全量返回，无分页，取 Top10）
 *  - 收款方式构成：queryReceivedWayStatistics（全量返回，无分页）
 * 两张图均为全量数据，不存在采样偏差，无需标注「基于当前页」。
 */
export default {
  name: 'DataReportOverviewCharts',
  components: {
    DsChartPanel,
    DsReportChart,
    DsEmptyState
  },
  data() {
    return {
      loadingFloor: false,
      loadingWay: false,
      floors: [],
      ways: []
    }
  },
  computed: {
    floorEmpty() {
      return !this.loadingFloor && (!this.floors || this.floors.length === 0)
    },
    wayEmpty() {
      return !this.loadingWay && this.chartSource.filter(item => item.value > 0).length === 0
    },
    topFloors() {
      const _list = (this.floors || []).slice()
      _list.sort((a, b) => (parseFloat(b.receivedFee) || 0) - (parseFloat(a.receivedFee) || 0))
      return _list.slice(0, 10)
    },
    floorBarOption() {
      const _list = this.topFloors
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            const _p = params && params[0]
            if (!_p) return ''
            return `${_p.name}: ${formatMoney(_p.value)}`
          }
        },
        grid: { left: 8, right: 24, top: 12, bottom: 8, containLabel: true },
        xAxis: { type: 'value' },
        yAxis: {
          type: 'category',
          inverse: true,
          data: _list.map(item => item.floorNum)
        },
        series: [{
          id: 'floor-received-rank',
          name: this.$t('dataReport.floorRankTitle'),
          type: 'bar',
          barMaxWidth: 18,
          label: {
            show: true,
            position: 'right',
            formatter: (params) => formatMoney(params.value, { decimals: 0 })
          },
          data: _list.map(item => parseFloat(item.receivedFee) || 0)
        }]
      }
    },
    chartSource() {
      const _fees = this.ways || []
      return _fees.map(item => ({
        name: item.name,
        value: parseFloat(item.receivedAmount) || 0
      }))
    },
    wayPieOption() {
      return {
        tooltip: {
          trigger: 'item',
          formatter: (params) => {
            const _percent = params.percent === undefined ? '' : ` (${params.percent}%)`
            return `${params.name}: ${formatMoney(params.value)}${_percent}`
          }
        },
        legend: {
          orient: 'vertical',
          left: 'left',
          top: 'middle'
        },
        series: [{
          id: 'overview-earned-way-pie',
          name: this.$t('dataReport.earnedWayChartTitle'),
          type: 'pie',
          radius: ['45%', '70%'],
          center: ['62%', '50%'],
          label: { formatter: '{b}\n{d}%' },
          labelLine: { length: 12, length2: 12 },
          data: this.chartSource
        }]
      }
    },
    floorSummary() {
      if (this.floorEmpty) return ''
      const _total = this.topFloors.reduce((sum, item) => sum + (parseFloat(item.receivedFee) || 0), 0)
      const _top = this.topFloors[0]
      if (!_top) return ''
      return `${this.$t('dataReport.floorRankSummary')}: ${formatMoney(_total)}，${_top.floorNum} ${formatMoney(_top.receivedFee)}`
    },
    waySummary() {
      if (this.wayEmpty) return ''
      const _total = this.chartSource.reduce((sum, item) => sum + item.value, 0)
      const _top = this.chartSource.slice().sort((a, b) => b.value - a.value)[0]
      return `${this.$t('dataReport.earnedWaySummary')}: ${formatMoney(_total)}，${_top.name} ${_top.value > 0 ? Math.round(_top.value / _total * 100) : 0}%`
    }
  },
  methods: {
    open(_param) {
      this._loadFloorData(_param)
      this._loadWayData(_param)
    },
    async _loadFloorData(_param) {
      this.loadingFloor = true
      try {
        const params = {
          communityId: _param.communityId,
          startDate: _param.startDate,
          endDate: _param.endDate
        }
        const { data } = await queryReceivedStatistics(params)
        this.floors = data || []
      } catch (error) {
        console.error('Failed to load floor rank data:', error)
        this.floors = []
      } finally {
        this.loadingFloor = false
      }
    },
    async _loadWayData(_param) {
      this.loadingWay = true
      try {
        const params = {
          communityId: _param.communityId,
          startDate: _param.startDate,
          endDate: _param.endDate
        }
        const { data } = await queryReceivedWayStatistics(params)
        this.ways = data || []
      } catch (error) {
        console.error('Failed to load way data:', error)
        this.ways = []
      } finally {
        this.loadingWay = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.data-report-overview {
  margin-bottom: var(--ds-spacing-md);
}
</style>
